import React, { useEffect } from "react";
import { Tabs, Form, Col, Row, message, FormInstance, TabsProps } from "antd";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ObjectAndDataRow, objectsAndDataStore } from "../Stores/ObjectsAndDataStore";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ModuleHeader } from "Pages/Project/Components/ModuleHeader";
import { isEmpty, uniqBy } from "lodash";
import { CreateNewObjectTypeModal, getAttributeFormItem } from "../Components/CreateNewObjectTypeModal";
import { BreadcrumpNavigation } from "../Components/BreadcrumpNavigation";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { Button } from "Components/General/Button";
import { Loader } from "Components/General/Loader";
import { SharedAttachmentsTable } from "Pages/Project/Documents/SharedAttachments/SharedAttachmentsTable";
import { projectsDataStore } from "Stores";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { stringSort } from "utils/defaultSort";

interface SubObjectsAndDataProps extends WithModal {}
const formRef = React.createRef<FormInstance>();
export const SubObjectsAndData = withModal(
  observer(({ showModal }: SubObjectsAndDataProps) => {
    const { t } = useTranslation([I18N_NS.ADMIN, I18N_NS.PROJECT]);
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
      (async () => {
        await objectsAndDataStore.fetchObjectData(+params.id, +params.key);

        if (params.tab === "attachments") {
          onTabChange("__attachments");
        } else {
          onTabChange(params.tab ? params.tab : "" + t("Direct attributes"));
        }
      })();
    }, [JSON.stringify(params)]);

    const [activeTab, setActiveTab] = React.useState("" + t("Direct attributes"));
    const onTabChange = (key) => {
      setActiveTab(key);
    };

    const { objectData } = objectsAndDataStore;
    const initialValues = {};
    const objectDataInfo = (objectData?.entries as Array<never>)?.reduce((array, item, index) => {
      objectData.columns.forEach((colName) => {
        const attribute = objectData.attributes.find((a) => a.name === colName);
        array.push(getAttributeFormItem(attribute, index, t, initialValues, item[colName], !objectData?.hasWriteAcl));
      });
      return array;
    }, []);

    useEffect(() => {
      console.log("reset", initialValues);
      formRef.current?.resetFields();
      formRef.current?.setFieldsValue(objectsAndDataStore.objectData.entries[0]);
    }, [JSON.stringify(initialValues)]);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const createObjectModal = async (nameSingleObject?: string, tableId) => {
      await objectsAndDataStore.getAllAttributes(tableId);
      showModal(CreateNewObjectTypeModal, {
        modalProps: {
          footer: null,
          width: 800,
          title: t("New {{name}}", { name: nameSingleObject }),
          maskClosable: false,
        },
        componentProps: {
          createNewAttributes: objectsAndDataStore.createNewAttributes,
          currentTableId: +params.id,
          tableId: tableId,
          referenceId: +params.key,
          attributes: objectsAndDataStore?.currentAttributes,
          isSubObject: true,
        },
      });
    };

    const uniqData = uniqBy(
      objectsAndDataStore.objectData?.referencedTables?.map((item) => {
        return item.data;
      }),
      "id"
    );

    const handleRowClick = (
      table: API_ValueTable,
      params: { rowData: ObjectAndDataRow; index: number; event: React.MouseEvent }
    ) => {
      event.preventDefault();
      navigate(`/project/${projectsDataStore.projectId}/values/${table.id}/sub-object/${params.rowData.key}`);
    };

    const renderReferenceListTabsTest: TabsProps["items"] = uniqData.map((item) => {
      const viewColumns = item.attributes.filter((a) => a.searchResultColumn).map((a) => a.name);

      const columns: TableColumnProps<never>[] = viewColumns?.map((column) => {
        return {
          label: column,
          dataKey: column,
          width: 300,
          sorter: (a, b) => stringSort(a, b, column),
          cellRenderer: (data) => (
            <Link
              target="_blank"
              style={{ display: "block" }}
              to={`/project/${projectsDataStore.projectId}/values/${item.id}/sub-object/${data.rowData.key}`}
            >
              {data.rowData[data.dataKey]}
            </Link>
          ),
        };
      });

      return {
        key: item.name,
        label: item.name,
        children:
          objectsAndDataStore.state === "LOADING" || activeTab !== item.name || !item?.entries ? (
            <Loader />
          ) : (
            <>
              <div className="scrollable-table-wrapper">
                <Row style={{ justifyContent: "flex-end", marginBottom: 20 }}>
                  <Button
                    disabled={!item.hasWriteAcl}
                    faIcon="plus"
                    type="primary"
                    onClick={() => createObjectModal(item.nameSingleObject, item.id)}
                  >
                    {t("New {{name}}", { name: item.nameSingleObject })}
                  </Button>
                </Row>
                <Row>
                  <ModuleHeader
                    title={t("{{count}} {{name}} found", {
                      count: item.entryCount,
                      name: item.name,
                    })}
                    style={{ flexGrow: 1, justifyContent: "center" }}
                  />
                </Row>

                <ScrollableTable<never>
                  rowKey={"key"}
                  stretchHeight={true}
                  data={item.entries as Array<never>}
                  columns={columns}
                  onRowClick={(params) => handleRowClick(item, params)}
                  resizeableHeaders={"OaD_" + item.id}
                />
              </div>
            </>
          ),
      };
    });

    const handleSubmit = async (values) => {
      await objectsAndDataStore.saveAttributes(+params.id, +params.key, values);
      message.success(t("Success"));
    };

    const { setChangedValues, hasChangedValues } = objectsAndDataStore;
    return (
      <>
        <Row>
          <BreadcrumpNavigation activeSubTab={activeTab} navigate={navigate} location={location} params={params} />
        </Row>
        <Row>
          <ModuleHeader title={`Object type - ${objectData?.name}`} />
        </Row>
        <Tabs
          defaultActiveKey={"" + t("Direct attributes")}
          activeKey={activeTab}
          onChange={onTabChange}
          className="views-module-tabs"
          items={[
            {
              key: t("Direct attributes"),
              label: t("Direct attributes"),
              children:
                isEmpty(objectDataInfo) || objectsAndDataStore.initState !== 2 ? (
                  <Loader />
                ) : (
                  <div style={{ maxHeight: "calc(100vh - 280px)" }}>
                    <Form
                      className="ant-form-item-with-postfix"
                      ref={formRef}
                      initialValues={initialValues}
                      onFinish={handleSubmit}
                      onValuesChange={(changedValues) => setChangedValues(changedValues)}
                    >
                      <div style={{ maxHeight: "calc(100vh - 340px)", overflow: "auto", width: "60%" }}>
                        {objectDataInfo}
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20, width: "60%" }}>
                        <Button
                          disabled={!objectData?.hasWriteAcl || !hasChangedValues}
                          htmlType="submit"
                          type="primary"
                        >
                          {t("Save")}
                        </Button>
                      </div>
                    </Form>
                  </div>
                ),
            },
            ...renderReferenceListTabsTest,
            {
              label: t("Attachments ({{number}})", { number: objectData?.attachments.items.length }),
              key: "__attachments",
              children: (
                <Col span={16}>
                  <SharedAttachmentsTable
                    editMode={objectData?.hasWriteAcl}
                    data={objectData?.attachments}
                    addFiles={(fileIds: []) =>
                      objectsAndDataStore.createAttachment(objectData?.id, +params.key, fileIds)
                    }
                    removeFiles={(fileIds: []) =>
                      objectsAndDataStore.removeAttachment(objectData?.id, +params.key, fileIds)
                    }
                  />
                </Col>
              ),
            },
          ]}
        />
      </>
    );
  })
);
